import type { ReactNode } from "react";

import { Rosette } from "../ornament";
import "./page-state.css";

export type PageTone = "missing" | "error" | "offline";

/**
 * A WHOLE PAGE THAT CANNOT BE SHOWN — «قاب خالی». The site is a gallery, so
 * a page that is not there is drawn as one: an empty gilt frame hung on its
 * wire under a spotlight, the rosette faint on its canvas and the reason in
 * the middle (a 404, a broken load, no connection), with a museum wall label
 * beside it — what happened, what to do, and the error code when there is one.
 *
 * Shared by the route boundary (`[lang]/error.tsx`), the 404s, a page's failed
 * server load (`LoadFailed`) and the offline fallback. Server-safe: the
 * actions are passed in, so the caller decides what is interactive.
 */
export default function PageState({ tone = "missing", glyph, eyebrow, title, body, actions, detail, headingLevel = 1 }: {
  tone?: PageTone;
  /** What hangs in the frame: a numeral (۴۰۴) or an icon. */
  glyph: ReactNode;
  eyebrow?: string;
  title: string;
  body?: string;
  actions?: ReactNode;
  /** A code the user can report (an error digest, an HTTP status). Shown verbatim; never invented. */
  detail?: { label: string; value: string };
  /** 1 when the state is the page; 2 when it sits under a page heading. */
  headingLevel?: 1 | 2;
}) {
  const H = headingLevel === 1 ? "h1" : "h2";
  return (
    <section className="ps" data-tone={tone} role={tone === "error" ? "alert" : undefined}>
      <div className="ps-stage">
        <figure className="ps-frame" aria-hidden>
          <svg className="ps-wire" viewBox="0 0 120 34" fill="none"><path d="M8 33 L60 4 L112 33" /><circle cx="60" cy="4" r="3" /></svg>
          <span className="ps-canvas">
            <Rosette className="ps-rosette" />
            <span className="ps-glyph">{glyph}</span>
          </span>
          <span className="ps-corners" />
        </figure>
        <div className="ps-label">
          {eyebrow ? <span className="ps-eyebrow">{eyebrow}</span> : null}
          <H>{title}</H>
          {body ? <p>{body}</p> : null}
          {actions ? <div className="ps-acts">{actions}</div> : null}
          {detail ? <p className="ps-detail">{detail.label} <code>{detail.value}</code></p> : null}
        </div>
      </div>
    </section>
  );
}
